import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type NavigateOptions, type To, useLocation, useNavigate, useParams } from 'react-router-dom';
import DraggableBottomSheet from '../components/DraggableBottomSheet';
import ScreenContainer from '../components/ScreenContainer';
import HeaderActionsMenu from '../components/HeaderActionsMenu';
import ConfirmationDialog from '../components/ConfirmationDialog';
import TagSelector from '../components/TagSelector';
import TransactionDetailView from '../components/TransactionDetailView';
import { useNavigation } from '../hooks/useNavigation';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { deleteTransactionAsync, updateTransactionTag } from '../store/slices/transactionsSlice';
import { fetchTags, fetchTagsByType, selectTagMap } from '../store/slices/tagsSlice';
import * as apiService from '../services/apiService';
import type { Transaction } from '../types';

interface TransactionDetailsRouteState {
  transaction?: Transaction;
}

interface TransactionDetailsScreenProps {
  getTransactionSplitPath?: (transaction: Transaction) => To;
  getTransactionNavigationState?: (transaction: Transaction) => NavigateOptions['state'];
  fallbackListPath?: To;
}

const findTransactionById = (transactions: Transaction[], targetId: number): Transaction | null => {
  for (const transaction of transactions) {
    if (transaction.id === targetId) {
      return transaction;
    }

    const subTransaction = transaction.subTransactions?.find((sub) => sub.id === targetId);
    if (subTransaction) {
      return subTransaction;
    }
  }

  return null;
};

const TransactionDetailsScreen: React.FC<TransactionDetailsScreenProps> = ({
  getTransactionSplitPath,
  getTransactionNavigationState,
  fallbackListPath = '/transactions',
}) => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { goBack, navigateTo } = useNavigation();
  const dispatch = useAppDispatch();

  const parsedTransactionId = Number(transactionId);
  const isTransactionIdValid = Number.isFinite(parsedTransactionId);
  const routeState = location.state as TransactionDetailsRouteState | null;
  const routeTransaction =
    routeState?.transaction && routeState.transaction.id === parsedTransactionId
      ? routeState.transaction
      : null;

  const { transactions, tagUpdateStatus } = useAppSelector((state) => state.transactions);
  const {
    debitTags,
    creditTags,
    status: tagsStatus,
    debitStatus,
    creditStatus
  } = useAppSelector((state) => state.tags);
  const tagMap = useAppSelector(selectTagMap);

  const [isTagSelectorOpen, setIsTagSelectorOpen] = useState<boolean>(false);
  const [selectedTransactionForTagId, setSelectedTransactionForTagId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);

  useEffect(() => {
    if (tagsStatus === 'idle') {
      dispatch(fetchTags());
    }
  }, [dispatch, tagsStatus]);

  const resolvedTransaction = useMemo(() => {
    if (!isTransactionIdValid) {
      return null;
    }

    return findTransactionById(transactions, parsedTransactionId) ?? routeTransaction;
  }, [isTransactionIdValid, parsedTransactionId, routeTransaction, transactions]);

  const selectedTransactionForTag = useMemo(() => {
    if (selectedTransactionForTagId == null) {
      return null;
    }

    const fromStore = findTransactionById(transactions, selectedTransactionForTagId);
    if (fromStore) {
      return fromStore;
    }

    if (resolvedTransaction?.id === selectedTransactionForTagId) {
      return resolvedTransaction;
    }

    return null;
  }, [selectedTransactionForTagId, transactions, resolvedTransaction]);

  const tagSelectorTransactionType = selectedTransactionForTag?.type ?? resolvedTransaction?.type;
  const tagSelectorAvailableTags = useMemo(
    () => (tagSelectorTransactionType === 'CREDIT' ? creditTags : debitTags),
    [tagSelectorTransactionType, creditTags, debitTags]
  );

  useEffect(() => {
    if (!tagSelectorTransactionType) {
      return;
    }

    if (tagSelectorTransactionType === 'CREDIT' && creditStatus === 'idle') {
      dispatch(fetchTagsByType('CREDIT'));
      return;
    }

    if (tagSelectorTransactionType === 'DEBIT' && debitStatus === 'idle') {
      dispatch(fetchTagsByType('DEBIT'));
    }
  }, [dispatch, tagSelectorTransactionType, creditStatus, debitStatus]);

  const closeDetailScreen = useCallback(() => {
    if (window.history.length > 1) {
      goBack();
      return;
    }

    navigate(fallbackListPath, { replace: true });
  }, [fallbackListPath, goBack, navigate]);

  const closeTagSelector = useCallback(() => {
    setIsTagSelectorOpen(false);
    setSelectedTransactionForTagId(null);
  }, []);

  const openTagSelector = useCallback((transaction: Transaction, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTransactionForTagId(transaction.id);
    setIsTagSelectorOpen(true);
  }, []);

  const openSplitView = useCallback((transaction: Transaction) => {
    navigateTo(getTransactionSplitPath?.(transaction) ?? `/transactions/${transaction.id}/split`, {
      state: getTransactionNavigationState?.(transaction) ?? { transaction }
    });
  }, [getTransactionNavigationState, getTransactionSplitPath, navigateTo]);

  const openSourceEmail = useCallback(async (transaction: Transaction) => {
    const messageId = transaction.emailMessageId?.trim();
    if (!messageId) {
      return;
    }

    try {
      const email = await apiService.fetchEmailByMessageId(messageId);
      if (email) {
        navigateTo(`/emails/${email.id}`);
      }
    } catch (error) {
      console.error('Failed to open source email:', error);
    }
  }, [navigateTo]);

  const handleUpdateTag = useCallback(async (newTag: string | null) => {
    if (tagUpdateStatus === 'loading') {
      return;
    }

    if (!selectedTransactionForTag) {
      return;
    }

    dispatch(updateTransactionTag({
      transactionId: selectedTransactionForTag.id,
      newTag,
      originalTransaction: selectedTransactionForTag,
    })).catch((error: unknown) => {
      console.error('Failed to update tag via Redux:', error);
    });
  }, [dispatch, selectedTransactionForTag, tagUpdateStatus]);

  const handleRequestDeleteTransaction = useCallback(() => {
    setShowDeleteConfirmation(true);
  }, []);

  const handleConfirmDeleteTransaction = useCallback(async () => {
    if (!resolvedTransaction?.id) {
      return;
    }

    setIsDeleting(true);
    try {
      await dispatch(deleteTransactionAsync({ transactionId: resolvedTransaction.id })).unwrap();
      setShowDeleteConfirmation(false);
      closeDetailScreen();
    } catch (error) {
      console.error('Failed to dispatch transaction delete:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [closeDetailScreen, dispatch, resolvedTransaction]);

  if (!isTransactionIdValid) {
    return (
      <ScreenContainer title="Transaction Details">
        <div className="mx-auto w-full max-w-sm px-6 pt-12 text-center text-muted-foreground">
          Invalid transaction selected.
        </div>
      </ScreenContainer>
    );
  }

  const headerAction = resolvedTransaction ? (
    <HeaderActionsMenu
      buttonAriaLabel="Open transaction actions"
      items={[
        {
          label: isDeleting ? 'Deleting transaction...' : 'Delete transaction',
          onSelect: () => {
            handleRequestDeleteTransaction();
          },
          disabled: isDeleting,
          destructive: true,
        },
      ]}
    />
  ) : null;

  return (
    <ScreenContainer title="Transaction Details" headerAction={headerAction}>
      {resolvedTransaction ? (
        <div className="mx-auto w-full max-w-sm h-full">
          <TransactionDetailView
            transaction={resolvedTransaction}
            tagMap={tagMap}
            onTagClick={openTagSelector}
            onSourceEmailClick={openSourceEmail}
            onManageSplit={openSplitView}
          />
        </div>
      ) : (
        <div className="mx-auto w-full max-w-sm px-6 pt-12 text-center text-muted-foreground">
          Transaction not found.
        </div>
      )}

      <DraggableBottomSheet
        isOpen={isTagSelectorOpen}
        onClose={closeTagSelector}
        title="Tag Transaction"
        closeLoading={tagUpdateStatus === 'loading'}
      >
        <TagSelector
          onSelectTag={handleUpdateTag}
          availableTags={tagSelectorAvailableTags}
          tagMap={tagMap}
          currentTagName={selectedTransactionForTag?.tag}
          transaction={selectedTransactionForTag ?? undefined}
          disabled={tagUpdateStatus === 'loading'}
        />
      </DraggableBottomSheet>

      <ConfirmationDialog
        isOpen={showDeleteConfirmation}
        title="Delete Transaction"
        message="Are you sure you want to delete this transaction? This action cannot be undone."
        confirmLabel="Delete"
        destructive
        isProcessing={isDeleting}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          void handleConfirmDeleteTransaction();
        }}
      />
    </ScreenContainer>
  );
};

export default TransactionDetailsScreen;
